/**
 * Portable Ledger Export — the governance moat, made provable OUTSIDE the app.
 *
 * A tamper-evident ledger only matters if a customer can hand an auditor a
 * file and let them verify it with zero trust in (or access to) Argo. This
 * module turns the in-app chain (src/lib/ledger/verifyChain.ts) into a
 * self-describing, forwardable JSON artifact — the SealedLedgerExport — plus
 * a shared `verifyExport` walk that both the in-app tests and the standalone
 * external verifier (scripts/verify-ledger.mjs) exercise.
 *
 * Integrity contract: hashing is the SAME FNV-1a chain digest the store
 * sealed with — imported from verifyChain.ts, never reimplemented — so a
 * chain that verifies in-app verifies identically from the exported file.
 * Each exported entry carries its payload as `canonical`: deterministic,
 * key-sorted JSON, so byte-identical exports of the same ledger are
 * guaranteed regardless of object insertion order.
 */

import { fnvHash, type LedgerEntry } from './verifyChain';

// ---------------------------------------------------------------------------
// Types — the portable artifact
// ---------------------------------------------------------------------------

/** One sealed link as it appears in the export file. */
export type SealedExportEntry = {
  /** Position in the chain, oldest-first, starting at 0. */
  index: number;
  /** The sealed digest stored on the entry. */
  hash: string;
  /** The previous entry's hash ('' for genesis). */
  prevHash: string;
  /** Key-sorted JSON of the payload the digest covers. */
  canonical: string;
};

/** The complete, forwardable audit artifact. */
export type SealedLedgerExport = {
  version: 1;
  tenant: string;
  /** Unix ms timestamp the export was taken. */
  exportedAt: number;
  /** The prevHash the chain starts from ('' for a genesis-rooted chain). */
  genesisHash: string;
  /** The hash of the newest sealed entry ('' when the ledger is empty). */
  chainHead: string;
  entryCount: number;
  entries: SealedExportEntry[];
  /** Self-describing so an external verifier knows how to recompute. */
  manifest: { algo: 'fnv1a'; canonicalization: 'sorted-json' };
};

/** Outcome of re-walking an exported chain. */
export type ExportVerifyResult = {
  ok: boolean;
  /** Index of the first broken entry (absent for structural/manifest failures). */
  brokenIndex?: number;
  /** Human-readable cause of the first failure. */
  reason?: string;
};

// ---------------------------------------------------------------------------
// Canonicalization — deterministic, key-sorted JSON
// ---------------------------------------------------------------------------

/**
 * Recursively rebuild a JSON-safe value with object keys in sorted order so
 * JSON.stringify emits a deterministic string. Arrays keep their order (order
 * is meaningful there); primitives pass through.
 *
 * NOTE: scripts/verify-ledger.mjs inlines a byte-identical copy of this
 * function (it must have ZERO src imports). Change both together.
 */
function sortKeysDeep(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeysDeep);
  if (typeof value === 'object' && value !== null) {
    const src = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(src).sort()) {
      // JSON.stringify drops undefined-valued keys anyway; skip them here so
      // {a: undefined} and {} canonicalize identically.
      if (src[key] !== undefined) out[key] = sortKeysDeep(src[key]);
    }
    return out;
  }
  return value;
}

/**
 * Deterministic key-sorted JSON of one sealed entry's payload — the exact
 * bytes stored in the export's `canonical` field. Order-independent: the
 * same payload with keys in any insertion order yields an identical string.
 */
export function canonicalizeEntry(entry: LedgerEntry): string {
  return JSON.stringify(sortKeysDeep(entry.payload));
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

/**
 * Shape a tenant's sealed chain (as produced by verifyChain's
 * `toLedgerEntries`) into the portable artifact. Pure: callers read entries
 * via the existing store getters and pass them in. Entries are re-sorted by
 * index so the file is always oldest-first regardless of caller order.
 */
export function exportLedger(
  entries: LedgerEntry[],
  meta: { tenant: string; exportedAt: number },
): SealedLedgerExport {
  const ordered = [...entries].sort((a, b) => a.index - b.index);
  const first = ordered[0];
  const last = ordered[ordered.length - 1];
  return {
    version: 1,
    tenant: meta.tenant,
    exportedAt: meta.exportedAt,
    genesisHash: first ? first.prevHash : '',
    chainHead: last ? last.hash : '',
    entryCount: ordered.length,
    entries: ordered.map((e) => ({
      index: e.index,
      hash: e.hash,
      prevHash: e.prevHash,
      canonical: canonicalizeEntry(e),
    })),
    manifest: { algo: 'fnv1a', canonicalization: 'sorted-json' },
  };
}

/**
 * Pretty, canonical (key-sorted) JSON — the exact download payload. Sorted
 * keys mean two exports of the same ledger are byte-identical, so auditors
 * can diff them.
 */
export function serializeExport(exp: SealedLedgerExport): string {
  return JSON.stringify(sortKeysDeep(exp), null, 2);
}

// ---------------------------------------------------------------------------
// Verification — the ONE code path tests, UI, and the external script share
// ---------------------------------------------------------------------------

/**
 * Re-walk an exported chain and report the FIRST failure:
 *   1. manifest must describe the scheme we know how to recompute;
 *   2. entryCount must match the entries actually present;
 *   3. per entry: contiguous indices from 0, prevHash linkage (genesis =
 *      genesisHash), and stored hash === fresh FNV-1a of prevHash + the
 *      parsed canonical payload — so edits, splices, and reorders all surface;
 *   4. chainHead must equal the last entry's hash (and genesisHash the first
 *      entry's prevHash).
 * An empty export (entryCount 0, chainHead '') is honestly ok.
 *
 * scripts/verify-ledger.mjs is a line-for-line port of this walk (zero src
 * imports); keep the two in lockstep.
 */
export function verifyExport(exp: SealedLedgerExport): ExportVerifyResult {
  if (exp.version !== 1) {
    return { ok: false, reason: `unsupported export version ${String(exp.version)}` };
  }
  if (exp.manifest?.algo !== 'fnv1a' || exp.manifest?.canonicalization !== 'sorted-json') {
    return { ok: false, reason: 'unknown manifest (expected fnv1a + sorted-json)' };
  }
  if (!Array.isArray(exp.entries) || exp.entryCount !== exp.entries.length) {
    return {
      ok: false,
      reason: `entryCount mismatch: declared ${exp.entryCount}, found ${exp.entries?.length ?? 0}`,
    };
  }

  let prevHash = exp.genesisHash ?? '';
  for (let i = 0; i < exp.entries.length; i++) {
    const e = exp.entries[i];
    // 1. Index continuity — a gap or out-of-order entry means a link was
    //    removed or spliced.
    if (e.index !== i) {
      return { ok: false, brokenIndex: i, reason: `index mismatch: expected ${i}, got ${e.index}` };
    }
    // 2. Link to predecessor.
    if (e.prevHash !== prevHash) {
      return {
        ok: false,
        brokenIndex: i,
        reason: `prevHash mismatch: expected ${prevHash || "''"}, got ${e.prevHash || "''"}`,
      };
    }
    // 3. The entry's own seal, recomputed from its canonical payload.
    let payload: unknown;
    try {
      payload = JSON.parse(e.canonical);
    } catch {
      return { ok: false, brokenIndex: i, reason: 'canonical payload is not valid JSON' };
    }
    const recomputed = fnvHash(e.prevHash, payload);
    if (e.hash !== recomputed) {
      return {
        ok: false,
        brokenIndex: i,
        reason: `hash mismatch: recomputed ${recomputed}, stored ${e.hash}`,
      };
    }
    prevHash = e.hash;
  }

  // 4. Header claims must match the walked chain.
  if (exp.chainHead !== prevHash) {
    return {
      ok: false,
      reason: `chainHead mismatch: walked to ${prevHash || "''"}, declared ${exp.chainHead || "''"}`,
    };
  }

  return { ok: true };
}
